import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, AlertTriangle, Users, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const STATUS_STYLE = {
  sent: "text-green-400", partial: "text-yellow-400", failed: "text-red-400",
  skipped: "text-neutral-400", queued: "text-[#D4AF37]",
};

export default function EmailMarketing() {
  const [audiences, setAudiences] = useState([]);
  const [status, setStatus] = useState({ email_enabled: false });
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ audience: "reserve", subject: "", heading: "", body_text: "", cta_label: "", cta_url: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/marketing/audiences").then(({ data }) => setAudiences(data)).catch(() => {});
    api.get("/marketing/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/marketing/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.subject.trim() || !form.body_text.trim()) { toast.error("Subject and message are required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/campaigns", form);
      toast.success(`Campaign queued to ${data.recipient_count} recipient${data.recipient_count === 1 ? "" : "s"}.`);
      setForm({ ...form, subject: "", heading: "", body_text: "", cta_label: "", cta_url: "" });
      setTimeout(load, 1400);
    } catch {
      toast.error("Could not send campaign.");
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="dash-marketing">
      <h1 className="font-display text-3xl font-bold">Email Marketing</h1>
      <p className="mt-2 text-sm text-neutral-400">Broadcast branded emails to your waitlists and members. Track results below.</p>

      {!status.email_enabled && (
        <div className="mt-5 flex items-start gap-3 border border-yellow-500/40 bg-yellow-500/5 p-4" data-testid="marketing-email-warning">
          <AlertTriangle size={18} className="text-yellow-400 mt-0.5 shrink-0" />
          <p className="text-sm text-yellow-200/90">Email sending isn't active yet. Add your <span className="font-semibold">RESEND_API_KEY</span> to switch it on — campaigns you send now will be logged as "skipped" until then.</p>
        </div>
      )}

      {status.email_enabled && !status.sender_verified && (
        <div className="mt-5 flex items-start gap-3 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4" data-testid="marketing-domain-warning">
          <AlertTriangle size={18} className="text-[#D4AF37] mt-0.5 shrink-0" />
          <p className="text-sm text-neutral-300">Email is live, but you're sending from Resend's shared address (<span className="font-mono text-xs">{status.sender_email}</span>), so emails currently reach <span className="font-semibold text-[#D4AF37]">only your own Resend inbox</span>. Verify a domain at <span className="font-mono text-xs">resend.com/domains</span> and set a custom sender to reach your whole list.</p>
        </div>
      )}

      {/* audience picker */}
      <div className="mt-6">
        <Label className="text-neutral-300">Audience</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {audiences.map((a) => (
            <button key={a.key} type="button" onClick={() => setForm({ ...form, audience: a.key })} data-testid={`marketing-audience-${a.key}`}
              className={`flex items-center gap-2 px-3 py-2 text-sm border transition-colors ${form.audience === a.key ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-neutral-400 hover:border-white/40"}`}>
              <Users size={14} /> {a.label} <span className="font-mono text-xs opacity-70">({a.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* compose */}
      <div className="mt-6 grid gap-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-neutral-300">Subject line</Label>
            <Input value={form.subject} onChange={set("subject")} data-testid="marketing-subject" placeholder="Barbistry Reserve drops next week" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-neutral-300">Heading (optional)</Label>
            <Input value={form.heading} onChange={set("heading")} data-testid="marketing-heading" placeholder="First access is yours" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
        </div>
        <div>
          <Label className="text-neutral-300">Message</Label>
          <Textarea value={form.body_text} onChange={set("body_text")} data-testid="marketing-body" rows={6} placeholder="Write your message. Each line becomes a paragraph." className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-neutral-300">Button label (optional)</Label>
            <Input value={form.cta_label} onChange={set("cta_label")} data-testid="marketing-cta-label" placeholder="Get first access" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-neutral-300">Button link (optional)</Label>
            <Input value={form.cta_url} onChange={set("cta_url")} data-testid="marketing-cta-url" placeholder="https://barbistry.com/reserve" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
        </div>
        <Button onClick={send} disabled={busy} data-testid="marketing-send" className="justify-self-start rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold px-8 group">
          {busy ? "Sending…" : <>Send campaign <Send size={16} className="ml-2 transition-transform group-hover:translate-x-1" /></>}
        </Button>
      </div>

      {/* history */}
      <div className="mt-12">
        <h2 className="font-display text-xl font-semibold">Campaign history</h2>
        {campaigns.length === 0 ? (
          <p className="mt-4 text-neutral-500 text-sm">No campaigns sent yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto border border-white/10">
            <table className="w-full text-sm min-w-[640px]" data-testid="marketing-history">
              <thead><tr className="bg-[#0a0a0a] border-b border-white/10 text-left text-xs font-mono uppercase text-neutral-400">
                <th className="p-3">Subject</th><th className="p-3">Audience</th><th className="p-3">Recipients</th><th className="p-3">Sent</th><th className="p-3">Status</th><th className="p-3">Date</th>
              </tr></thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className="border-b border-white/5" data-testid={`campaign-row-${c.id}`}>
                    <td className="p-3 text-white">{c.subject}</td>
                    <td className="p-3 capitalize text-neutral-300">{c.audience.replace("_", " ")}</td>
                    <td className="p-3 text-neutral-400">{c.recipient_count}</td>
                    <td className="p-3 text-neutral-300">{c.sent_count}{c.failed_count ? ` · ${c.failed_count} failed` : ""}</td>
                    <td className={`p-3 capitalize ${STATUS_STYLE[c.status] || "text-neutral-400"}`}>
                      {c.status === "sent" && <CheckCircle2 size={13} className="inline mr-1" />}{c.status}
                    </td>
                    <td className="p-3 text-neutral-500">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
